
import React from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';

const CitrullineSection = () => {
  return (
    <div className="bg-muted p-3 md:p-6 rounded-lg border border-border">
      <h3 className="text-lg md:text-xl font-semibold text-primary mb-2">L-Citrulline – 10 000 mg</h3>
      <p className="mb-3 text-sm md:text-base text-muted-foreground leading-relaxed">
        L-Citrulline er det mest effektive tilskuddet for å øke nitrogenoksid (NO) i kroppen. 
        Forskning av <a href="https://pubmed.ncbi.nlm.nih.gov/18056558/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">Moinard et al. (2008)</a> viser at kroppens evne til å omdanne citrulline til arginin maksimeres ved 10g. Doser over dette gir ingen ekstra effekt – du treffer det fysiologiske taket.
      </p>

      <div className="bg-muted p-3 md:p-4 rounded-lg border border-border mb-3">
        <h4 className="font-bold text-primary mb-2 text-sm md:text-base">Markedssnitt: ~3 506 mg. Peveo: 10 000 mg.</h4>
        <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
          De fleste konkurrenter oppgir «Citrulline Malate» på etiketten – men skjuler hvor mye som er ren L-citrulline og hvor mye som er billig eplesyre. Et produkt med 6g «Citrulline Malate 2:1» inneholder bare 4g ren L-citrulline. Vi oppgir 10g ren L-citrulline, pluss 5g eplesyre separat – full transparens.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-3 md:gap-4 mb-3">
        <div>
          <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Hvorfor nitrogenoksid er viktig:</h4>
          <ul className="list-disc list-inside space-y-1 md:space-y-2 text-sm md:text-base text-muted-foreground leading-relaxed">
            <li>Utvider blodkar (vasodilatasjon)</li>
            <li>Senker blodtrykket</li>
            <li>Øker oksygennivået i blodet</li>
            <li>Sender signaler til blodkarene om å utvide eller trekke seg sammen</li>
            <li>Sikrer at blod kan strømme effektivt til og fra hjertet</li>
          </ul>
          
          {/* YouTube video */}
          <div className="mt-3 md:mt-4 rounded-lg overflow-hidden shadow-lg border border-border">
            <AspectRatio ratio={16 / 9}>
              <iframe 
                src="https://www.youtube.com/embed/EoYhQIHmKoE" 
                title="Citrulline Benefits Explained"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </AspectRatio>
          </div>
        </div>
        <div>
          <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Dokumenterte effekter:</h4>
          <ul className="list-disc list-inside space-y-1 md:space-y-2 text-xs md:text-base text-muted-foreground leading-relaxed">
            <li>9% økt prestasjon i underkroppen <a href="https://afpt.no/fagstoff/er-citrulline-malate-det-nye-kreatinet-del-1" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[Artikkel]</a> <a href="https://pubmed.ncbi.nlm.nih.gov/25226311/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
            <li>19% økt prestasjon i overkroppen <a href="https://afpt.no/fagstoff/er-citrulline-malate-det-nye-kreatinet-del-2" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[Artikkel]</a> <a href="https://pubmed.ncbi.nlm.nih.gov/25674699/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
            <li>Redusert muskeltretthet</li>
            <li>40% reduksjon i muskelsmerter etter trening <a href="https://pubmed.ncbi.nlm.nih.gov/20386132/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[R]</a></li>
            <li>Opptil 53% økning i repetisjoner i siste sett sammenlignet med placebo</li>
          </ul>
          <p className="mt-3 text-sm text-primary">
            Kilde: <a href="https://pubmed.ncbi.nlm.nih.gov/20386132/" className="underline hover:text-primary" target="_blank" rel="noopener noreferrer">Pérez-Guisado & Jakeman (2010)</a>
          </p>
        </div>
      </div>
      
      {/* Citrulline image – systemiske effekter */}
      <div className="mt-4 md:mt-5 space-y-3 md:space-y-4">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start">
          <div className="flex-shrink-0 w-full md:w-1/2">
            <img 
              src="/lovable-uploads/e01b43fb-67c8-4135-bfb7-75c53a26efd0.png" 
              alt="Citrulline's systemiske effekter diagram" 
              className="w-full rounded-lg border border-border"
            />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-foreground mb-2 text-sm md:text-base">Citrulline's systemiske effekter i kroppen:</h4>
            <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
              Diagram som viser hvordan L-citrulline metaboliseres i kroppen for å øke nitrogenoksid produksjonen. 
              Merk at hele 5kg vannmelon inneholder kun rundt 3g L-citrulline <a href="https://pubmed.ncbi.nlm.nih.gov/30029482/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[R]</a>.
            </p>
            <div className="mt-2 md:mt-3 p-2 md:p-3 bg-muted rounded-lg border border-border">
              <h5 className="font-semibold text-primary text-xs md:text-sm mb-1 md:mb-2">Citrulline vs. Arginine:</h5>
              <p className="text-xs text-muted-foreground leading-relaxed">L-Citrulline er mer effektiv enn L-Arginine for å øke arginin-nivåer i kroppen. Oral L-Arginine blir nesten helt metabolisert av leveren, mens L-Citrulline omgår leveren og omdannes til arginin i nyrene <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1774314/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[R]</a>.</p>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground italic mt-3 leading-relaxed">
        L-Citrulline supplementering har vist å senke blodtrykket og gi aterogenisk-endotelial beskyttelse <a href="https://pubmed.ncbi.nlm.nih.gov/30029482/" className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">[R]</a>.
      </p>
    </div>
  );
};

export default CitrullineSection;
